/**
 * **文档通道** —— 宿主给**包贡献的编辑器**的那一份只读快照 + 受控写入口。
 *
 * ```text
 *   宿主（文档的唯一持有者）               包贡献的编辑器（打印包的页面编辑器…）
 *   nodes / edges / 选中 / 求值          ┌──────────────────────────────┐
 *        │                               │ 读 doc.nodes / doc.links      │
 *        ├── useEditorDoc() ─── doc ────►│ 写 doc.setValue / addNode /…  │
 *        │       （本文件）               └──────────────────────────────┘
 *        ▼
 *   commit（历史） / applyConnection（校验） / deleteNodesById（级联）
 * ```
 *
 * ## 为什么要有这一层（而不是把 `nodes` 直接传给包）
 *
 * `node-packages.md` §5「隔离」：包**不该**摸到内核的单例状态。
 * 直接把 `nodes` 那个 ref 递过去，等于把「宿主的状态形状」变成包的 API ——
 * 第二天就会有包去改 `node.data.model.rows[3].widget.value`，
 * 而那条路**绕过历史**：改得动、但撤销不回去。
 *
 * 所以这里做两件事，两件都不能省：
 *
 * ① **读**：投影成 `EditorNodeView`（id / type / 行值 / 图坐标），
 *    而不是把内部对象交出去。包看到的是**契约**，不是我们的数据结构；
 * ② **写**：每个写入口都转调宿主已有的那条路径（`commit` / `applyConnection` /
 *    `deleteNodesById`），所以包改的东西和用户手改的东西**走同一套历史与校验**。
 *
 * ## ★ 选中：**替换**语义，而且只有一条实现
 *
 * 页面编辑器里点一个框 = 「选中它，别的都取消」。这件事由
 * `graph/flowStore.ts` 的 `selectOnly()` 统一负责（加节点 / 粘贴也走它）：
 *
 * 它写的是文档里的 `selected` 标志（`selectOnly`）。这不是"绕过引擎"：
 * **选中的真源就是那几个布尔** —— 我们的画布不持有自己的节点副本
 * （`canvasState.getNodes()` 返回的就是宿主这一份，见那里的约束 ③
 * 「选中是文档的一部分」），所以写标志 == 应用一批 select 变更，
 * 而且**一个图窗口都没开时照样有效**（用户把唯一的 Area 切成了页面编辑器）。
 *
 * ⚠️ 曾经走的是 store 的 `setSelection` 提案，踩了两个坑：要么两批变更
 * （中间那一瞬间"什么都没选中"是真的），要么因为 store 读的是 **props**
 * 而让"刚建出来的节点"选不上（要等下一拍）。写标志没有这些时差。
 *
 * ⚠️ 反过来说：**不要**在这里加"位置 / 尺寸"之类的直写。那些字段有
 * 引擎侧的测量与发布语义（拖动期间就地写、松手发布，见 `onNodesChange`），
 * 直写会绕过它。选中没有—— 它就是文档里的几个布尔。
 */
import { computed } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowNode as Node } from '@/graph/flowTypes'
import { republish } from '@/graph/documentSync'
import { repeaterIndexFromHandle } from '@/node/rows'
import { selectOnly } from '@/graph/flowStore'
import type { EditorDocApi, EditorLinkView, EditorNodeView } from '@/types/editor'
import type { GraphNodeModel, NodeRow } from '@/types/node'

export interface EditorDocCtx {
  nodes: { value: Node[] }
  edges: { value: Edge[] }
  /** 选中态（宿主那份 —— 可能来自图引擎的 store） */
  selection: () => readonly string[]
  /** 选中时一起设的「活动节点」（属性面板跟的是它） */
  activeNodeId: { value: string | null }
  /**
   * 求值结果：节点 id → 端口 id → 值。
   *
   * 闭包注入（它声明在本文件被调用的位置之后）—— 只在这几个回调里读。
   */
  evalOutputs: () => ReadonlyMap<string, ReadonlyMap<string, unknown>>
  /** 谁提供这个节点类型（文档里逐节点记的 `pkg` 就是它） */
  pkgOf: (type: string) => string | undefined
  /** 记一步历史（**必须在改动之前**调，见 `useUndoRedo`） */
  commit: (label: string, coalesceMs?: number) => void
  /** 按 id 取模型（当前这一层） */
  modelOf: (id: string) => GraphNodeModel | undefined
  nextNodeId: (type: string) => string
  makeModelOfType: (type: string, id: string) => GraphNodeModel | null
  makeFlowNode: (model: GraphNodeModel, position: { x: number; y: number }) => Node
  /** 一条连线真正落到图上（宿主唯一那条，走类型 / 结构校验） */
  applyConnection: (conn: {
    source: string
    sourceHandle: string | null
    target: string
    targetHandle: string | null
  }) => void
  /** 结构校验（类型 + 自连 + 环）—— 和拖线用的是同一份 */
  isStructurallyValid: (
    sourceNodeId: string,
    sourceHandleId: string | null | undefined,
    targetNodeId: string,
    targetHandleId: string | null | undefined,
  ) => boolean
  /** 删一批节点（连同连线、级联、受保护节点都在宿主那条路里） */
  deleteNodes: (ids: readonly string[]) => void
  /** 视口中心（图坐标）—— 包不给落点时新节点摆这儿 */
  viewCenter: () => { x: number; y: number }
  /**
   * 「这个节点就地改过了」—— 换掉它的引用，让下游（求值 / 文档投影）重算。
   *
   * ⚠️ 就地改字段**不会**触发 Vue 的响应式，所以每条"直接写文档"的入口
   * 都必须显式收这个尾。宿主那边接的是 `republish`（`graph/documentSync.ts`）。
   */
  republishNode: (nodeId: string) => void
}

/**
 * 一个节点上**能被编辑器读到**的行值。
 *
 * ⚠️ 只有 `property` / `input` 这两种行有内联控件；`repeater` 是端口
 * （值是上游来的），`display` / `group` 没有值。
 */
function valuesOf(rows: readonly NodeRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const row of rows) {
    if (row.kind === 'property') out[row.id] = row.widget?.value
    else if (row.kind === 'input' && row.widget) out[row.id] = row.widget.value
  }
  return out
}

export function useEditorDoc(ctx: EditorDocCtx) {
  const { nodes, edges, selection, activeNodeId, commit, modelOf } = ctx

  /**
   * 「正在一次交互里」（`beginEdit` 到 `endEdit` 之间）。
   *
   * ⚠️ 是**模块级的一个布尔**而不是每次调用一个新的：`useEditorDoc` 只被
   * 宿主调用一次，而 begin/end 必须配到同一份状态上。做成 ref 没意义 ——
   * 它不参与渲染（没有界面该因为"正在拖动"而重画）。
   */
  let editing = false

  /*
   * ⚠️ 投影放在 `computed` 里，而且**读的是控件值本身**（不是 `rows` 的引用）——
   * 这样「节点图里改一个数值 → 页面编辑器跟着动」靠的是响应式依赖，
   * 不需要任何 watch / 版本号（那类约定必然漏，见 `editorRegistry.ts` 的教训）。
   */
  const docNodes = computed<EditorNodeView[]>(() =>
    nodes.value.map((node) => {
      const model = node.data?.model as GraphNodeModel | undefined
      return {
        id: node.id,
        type: model?.type ?? '',
        title: model?.label || model?.title || model?.type || node.id,
        values: valuesOf(model?.rows ?? []),
        position: { x: node.position?.x ?? 0, y: node.position?.y ?? 0 },
        ...(model ? { pkg: ctx.pkgOf(model.type) } : {}),
      }
    }),
  )

  const docLinks = computed<EditorLinkView[]>(() =>
    edges.value.map((edge) => ({
      id: edge.id,
      source: edge.source,
      sourceHandle: edge.sourceHandle ?? null,
      target: edge.target,
      targetHandle: edge.targetHandle ?? null,
    })),
  )

  /**
   * 改一个节点的控件值。
   *
   * ⚠️ `commit` **在改动之前**调（快照式历史记的是"改动前的现状"）——
   * 顺序反了的表现是「撤销之后回到的是改完的状态」。
   *
   * ⚠️ `editing` 期间**不提交**：那是"一次交互"（拖动）里的一帧，
   * 历史已经在 `beginEdit` 里记过一步了（见下面的说明）。
   */
  function setValue(nodeId: string, rowId: string, value: unknown, coalesceMs = 0): void {
    const row = modelOf(nodeId)?.rows.find((r) => r.id === rowId)
    if (!row || (row.kind !== 'property' && row.kind !== 'input') || !row.widget) return
    if (!editing) commit('修改参数', coalesceMs)
    row.widget.value = value
    /*
     * ★★ **改完必须"重新发布"这个节点**。
     *
     * 这一行是"面板读不到自己刚写的值"的**根因**，也是这一版最值钱的一行：
     *
     *   · `row.widget.value = value` 是**就地改一个普通对象的字段** ——
     *     Vue 不会因此通知任何人（节点模型不是响应式的）；
     *   · 于是 `nodes` 这个数组引用没变 → `evalModels` / `evalResult` /
     *     文档投影（`docNodes`）**全都不会重算** → 求值结果还是旧的；
     *   · 编辑器"看起来"跟着变了，是因为它读**字面量兜底**
     *     （`evaluated?.fit ?? node.values.fit`）—— 那条路读的是刚被改过的
     *     同一个对象，所以是新的；
     *   · 而面板读的是**求值结果**（连线的真值那一份）→ 永远看到旧值。
     *
     * 真机反馈"面板属性里也改不了『拉伸』，只能使用『完整放入』"就是它：
     * 用户选了拉伸，编辑器画对了，下拉框却弹回"完整放入"。
     *
     * 图那一边的控件更新走的是 `republish`（见 `graph/documentSync.ts` 的说明：
     * "宿主自己就地改了文档里的东西时的收尾动作"）—— 这条通道原来漏了这一步。
     * ⚠️ 改任何"就地写文档"的入口，都要配这一个收尾。
     */
    ctx.republishNode(nodeId)
  }

  /**
   * 一次交互 = 一步历史。
   *
   * 拖动是按帧写值的（一帧一次 `commit` 会塞进几十步历史，撤销一次只退回
   * 一帧）。所以把"这是同一次交互"这件事交给**编辑器**声明：
   * 它知道现在是在拖拽还是在输入。
   */
  function beginEdit(label: string): void {
    if (editing) return
    commit(label)
    editing = true
  }

  function endEdit(): void {
    editing = false
  }

  function addNode(
    type: string,
    at?: { x: number; y: number },
    values?: Readonly<Record<string, unknown>>,
  ): string | null {
    const id = ctx.nextNodeId(type)
    const model = ctx.makeModelOfType(type, id)
    if (!model) return null
    /*
     * 初值在**建之前**铺进模型：`makeModelOfType` 每实例深拷贝一份行
     * （见 `nodeTypes.ts` 的注释），改这里不会污染目录模板。
     */
    if (values) {
      for (const row of model.rows) {
        if (row.kind !== 'property' && row.kind !== 'input') continue
        if (!row.widget || !(row.id in values)) continue
        row.widget.value = values[row.id]
      }
    }
    commit('添加节点')
    nodes.value = [
      ...(nodes.value as unknown as Record<string, unknown>[]),
      ctx.makeFlowNode(model, at ?? ctx.viewCenter()) as unknown as Record<string, unknown>,
    ] as unknown as Node[]
    /* 和 `useAddNode.addNodeAt` 一致：建完选中 + 设为活动，属性面板才跟得过来 */
    select([id])
    return id
  }

  function connect(
    source: string,
    sourceHandle: string,
    target: string,
    targetHandle: string,
  ): boolean {
    if (!ctx.isStructurallyValid(source, sourceHandle, target, targetHandle)) return false
    commit('连线')
    ctx.applyConnection({ source, sourceHandle, target, targetHandle })
    return true
  }

  /**
   * 选中一批节点（**替换式**）。
   *
   * ⚠️ 走 `selectOnly`（和「加节点 / 粘贴」同一个入口）：它写的是文档里的
   * `selected` 标志，而那**就是**选中的真源 —— 我们的画布不持有自己的节点
   * 副本（`canvasState.getNodes()` 返回的就是宿主这一份），
   * 所以所有窗口下一拍都会看到。
   *
   * ⚠️ 不要在这里就地改**位置 / 尺寸**之类的字段：那些有引擎侧的测量与
   * 发布语义（拖动期间就地写、松手发布，见 `onNodesChange`）。选中没有 ——
   * 它就是文档里的几个布尔。
   */
  /**
   * **改一组连线的 `targetHandle`**（层序 / 页序都靠它）。
   *
   * ⚠️ 句柄**不是行值**，所以走不了 `setValue`：它是"这条边接在变参行的
   * 哪一个槽上"。这里改完必须 **republish**（就地改字段的老规矩，
   * 见 `graph/flowStore.ts` 的 `selectOnly` 那段说明）——
   * 不 republish 的话别的窗口看不到新的层序。
   */
  function setHandles(changes: { id: string; targetHandle: string }[]): void {
    if (changes.length === 0) return
    const byId = new Map(changes.map((change) => [change.id, change.targetHandle]))
    const next = edges.value.map((edge) =>
      byId.has(edge.id) ? { ...edge, targetHandle: byId.get(edge.id)! } : edge,
    )
    edges.value = republish(next, changes.map((change) => change.id))
  }

  /**
   * 把 `ownerId` 上 `rowId.N` 的顺序写成给定顺序。
   *
   * 入参是**完整顺序**（不是"谁挪了几位"）：调用方看到的顺序就是最终顺序，
   * 少一次"我算的序号和你想的是不是一个意思"。
   */
  function applyOrder(ownerId: string, rowId: string, orderedSourceIds: readonly string[]): void {
    const wanted = new Map(orderedSourceIds.map((source, index) => [source, `${rowId}.${index}`]))
    const changes = docLinks.value
      .filter((link) => link.target === ownerId && wanted.has(link.source))
      .filter((link) => link.targetHandle !== wanted.get(link.source))
      .map((link) => ({ id: link.id, targetHandle: wanted.get(link.source)! }))
    setHandles(changes)
  }

  /**
   * **挪一位**（层序 / 页序都是它）。
   *
   * ⚠️ 这个函数**只知道"某一行上的第几个槽"**，不知道 `items` / `pages`
   * 分别是什么 —— 行 id 与"谁是拥有者"都由包传进来（内核不许认识
   * 包里的节点类型，见 `EditorDocApi.reorderChildren`）。
   */
  function reorderChildren(ownerId: string, rowId: string, childId: string, delta: number): void {
    const order = docLinks.value
      .filter((link) => link.target === ownerId && repeaterIndexFromHandle(link.targetHandle, rowId) !== null)
      .sort(
        (a, b) =>
          (repeaterIndexFromHandle(a.targetHandle, rowId) ?? 0) - (repeaterIndexFromHandle(b.targetHandle, rowId) ?? 0),
      )
      .map((link) => link.source)
    const from = order.indexOf(childId)
    const to = from + delta
    if (from < 0 || to < 0 || to >= order.length) return
    const next = [...order]
    next.splice(from, 1)
    next.splice(to, 0, childId)
    commit('调整顺序')
    applyOrder(ownerId, rowId, next)
  }

  function select(ids: readonly string[]): void {
    /* ⚠️ 就地改的标志必须 republish（见 `selectOnly` 的说明） */
    const dirty = selectOnly(nodes.value as unknown as { id: string; selected?: boolean }[], ids)
    nodes.value = republish(nodes.value, dirty)
    activeNodeId.value = ids[0] ?? null
  }

  /**
   * ⚠️ 用 **getter**（不是把 `docNodes.value` 拷进去）：包组件在 `render` 里
   * 读 `doc.nodes`，依赖必须记在**那一次读**上；拷成普通数组的话，
   * 一次拷贝就把它和响应式断开了。
   */
  const doc: EditorDocApi = {
    get nodes() {
      return docNodes.value
    },
    get links() {
      return docLinks.value
    },
    get selection() {
      return selection()
    },
    get outputs() {
      return ctx.evalOutputs()
    },
    beginEdit,
    endEdit,
    setValue,
    addNode,
    removeNodes: (ids) => ctx.deleteNodes(ids),
    connect,
    reorderChildren,
    select,
  }

  return { doc }
}
